import React, { useState, useEffect } from 'react';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';

export const SettingsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [commissionRate, setCommissionRate] = useState(20);
  const [surgeMultiplier, setSurgeMultiplier] = useState(1.0);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const listAdminUsers = httpsCallable(functions, 'listAdminUsers');
        const result = await listAdminUsers();
        setAdmins(result.data.users || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load admin users');
      }
    };
    fetchAdmins();

    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'system_settings', 'config'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setCommissionRate(data.commissionRate || 20);
          setSurgeMultiplier(data.surgeMultiplier || 1.0);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchSettings();
  }, []);

  const addAdmin = async () => {
    if (!newAdminEmail) return;
    setLoading(true);
    try {
      const setAdminClaim = httpsCallable(functions, 'setAdminClaim');
      await setAdminClaim({ email: newAdminEmail });
      toast.success(`Admin added for ${newAdminEmail}`);
      setNewAdminEmail('');
      const listAdminUsers = httpsCallable(functions, 'listAdminUsers');
      const result = await listAdminUsers();
      setAdmins(result.data.users || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSystemSettings = async () => {
    try {
      await setDoc(doc(db, 'system_settings', 'config'), {
        commissionRate,
        surgeMultiplier,
        updatedAt: new Date(),
        updatedBy: auth.currentUser?.uid
      }, { merge: true });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Admin Users</h2>
        <div className="mb-4">
          <input
            type="email"
            placeholder="New admin email"
            className="bg-gray-700 border border-gray-600 rounded px-3 py-2 mr-2 w-64"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
          />
          <button
            onClick={addAdmin}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded"
          >
            {loading ? 'Adding...' : 'Add Admin'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-700">
              <tr><th className="px-4 py-2 text-left">Email</th><th className="px-4 py-2 text-left">UID</th><th className="px-4 py-2 text-left">Admin Since</th></tr>
            </thead>
            <tbody>
              {admins.map(admin => (
                <tr key={admin.uid} className="border-t border-gray-700">
                  <td className="px-4 py-2">{admin.email}</td>
                  <td className="px-4 py-2 font-mono text-xs">{admin.uid}</td>
                  <td className="px-4 py-2">{admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">System Settings</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Commission Rate (%)</label>
            <input
              type="number"
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 w-full"
              value={commissionRate}
              onChange={(e) => setCommissionRate(Number(e.target.value))}
              step="0.5"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Surge Multiplier (1 = normal)</label>
            <input
              type="number"
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 w-full"
              value={surgeMultiplier}
              onChange={(e) => setSurgeMultiplier(Number(e.target.value))}
              step="0.1"
              min="1"
            />
          </div>
        </div>
        <button
          onClick={saveSystemSettings}
          className="mt-4 bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
};
